import {
  OFFER_INVALID_REASON,
  OFFER_MEDIA_DEVICE_TYPE,
  OFFER_MULTIPLE_OPTIONS_PRODUCT_CATEGORY,
  OFFER_STATUS,
  OfferAvailabilityRestriction,
  OfferCoreProductMedia,
  OfferInvalidReasonType,
  OfferMultipleOptionsAddonProduct,
  OfferMultipleOptionsCoreProduct,
  OfferMultipleOptionsCoreProductMedia,
  OfferMultipleOptionsCoreProductSelectionsConfigWithProduct,
  OfferSingleOptionProductSelectionsConfigWithProduct,
  OfferStatusType
} from '@ag1/atlas';
import {
  ExtendedProductData,
  Languages,
  OfferInvalidReason404,
  OfferAddonProduct,
  OfferCoreProductType,
  OfferCoreProduct,
  SupportedCheckoutFlowType,
  PortalOffer,
  OfferShopifyProductData
} from '@portal/types/offer';
import { getFormattedShopifyId } from '@portal/shared/utils/shopify/ids';
import { assertNever } from '@portal/types/helpers';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { LocaleEnum } from '@portal/schema/locale';
import { SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53 } from '@portal/config/base';
import { StoreCode } from '@portal/schema/storeCode';
import {
  desktopMediaMap,
  mobileMediaMap,
  VARIANT_ID_MAP
} from '@portal/shared/components/ContentBlocks/Offer/BuildBundle/constants';
import {
  OFFER_INVALID_REASON_404,
  SUPPORTED_CHECKOUT_FLOW_TYPES,
  ValidLanguages
} from '@portal/constants/offer';
import { urlFor } from '../urlFor';
import { uniqBy } from '../uniqBy';

export const isSupportedCheckoutFlowType = (value: string): value is SupportedCheckoutFlowType =>
  SUPPORTED_CHECKOUT_FLOW_TYPES.includes(value as any);

export const isOfferStatus = (status: string): status is OfferStatusType =>
  Object.values(OFFER_STATUS).includes(status as OfferStatusType);

export const isValidLanguage = (lang: string): lang is Languages =>
  ValidLanguages.includes(lang as Languages);

export const isMultipleOptionsCoreProduct = (
  product: OfferCoreProduct | OfferMultipleOptionsCoreProduct
): product is OfferMultipleOptionsCoreProduct => {
  return (
    typeof product === 'object' &&
    product !== null &&
    'quantity' in product &&
    !Array.isArray(product.product_data)
  );
};

export const isMultipleOptionsConfig = (
  config:
    | OfferMultipleOptionsCoreProductSelectionsConfigWithProduct
    | OfferSingleOptionProductSelectionsConfigWithProduct
): config is OfferMultipleOptionsCoreProductSelectionsConfigWithProduct => {
  return typeof config === 'object' && config !== null && 'addons' in config;
};

export const getOfferAvailabilityRestriction = (
  offer: PortalOffer | null | undefined,
  locale: Languages
) => {
  const restriction = offer?.availability_restrictions?.find((i) => i.locales?.includes(locale));
  if (!restriction) {
    return null;
  }
  return restriction;
};

export const getOfferAvailabilityRestrictions = (
  offer: Pick<PortalOffer, 'availability_restrictions'>
) =>
  uniqBy(
    (offer.availability_restrictions || []).filter(
      (i) => i.locales && i.locales.length > 0
    ) as OfferAvailabilityRestriction[],
    (i) => i.locales[0]
  ).sort((a, b) => ValidLanguages.indexOf(a.locales[0]) - ValidLanguages.indexOf(b.locales[0]));

export const getOfferLocales = (offer: Pick<PortalOffer, 'availability_restrictions'>) =>
  getOfferAvailabilityRestrictions(offer).map((i) => i.locales[0]);

export const isOfferSubscriptionProductType = (type: string): type is OfferCoreProductType =>
  Object.values(OFFER_MULTIPLE_OPTIONS_PRODUCT_CATEGORY).includes(type as any);

export const getStoreCodeFromAtlasLocale = (locale: Languages): StoreCode => {
  switch (locale) {
    case 'de':
    case 'eu':
    case 'nl':
    case 'se':
    case 'es':
    case 'fr':
      return 'eu';

    case 'gb':
      return 'uk';

    case 'us':
    case 'apac-us':
      return 'us';

    // Has its own store, but uses "us" for now.
    case 'au':
      return 'us'; // 'au';

    // Has its own store, but uses "us" for now.
    case 'nz':
      return 'us'; // 'nz';

    case 'jp':
      return 'jp';

    default:
      return assertNever(locale, 'us');
  }
};

export const getLocaleFromAtlas = (atlasLocale: Languages): LocaleEnum => {
  switch (atlasLocale) {
    case 'de':
      return LocaleEnum.deEU;
    case 'eu':
      return LocaleEnum.enEU;
    case 'gb':
      return LocaleEnum.enUK;
    case 'nl':
      return LocaleEnum.nlEU;
    case 'se':
      return LocaleEnum.seEU;
    case 'us':
      return LocaleEnum.enUS;
    case 'es':
      return LocaleEnum.esEU;
    case 'fr':
      return LocaleEnum.frEU;
    case 'jp':
      return LocaleEnum.jaJP;
    case 'apac-us':
      return LocaleEnum.apacUS;
    case 'au':
      return LocaleEnum.enAU;
    case 'nz':
      return LocaleEnum.enNZ;
    default:
      return assertNever(atlasLocale, LocaleEnum.enUS);
  }
};

export const getLocaleForAtlas = (portalLocale: LocaleEnum): Languages => {
  switch (portalLocale) {
    case LocaleEnum.deEU:
    case LocaleEnum.deEud:
    case LocaleEnum.deQa:
      return 'de';
    case LocaleEnum.enEU:
    case LocaleEnum.enEud:
      return 'eu';
    case LocaleEnum.enUK:
    case LocaleEnum.enUkd:
      return 'gb';
    case LocaleEnum.nlEU:
      return 'nl';
    case LocaleEnum.seEU:
      return 'se';
    case LocaleEnum.enUS:
    case LocaleEnum.enDev:
    case LocaleEnum.enQa:
      return 'us';
    case LocaleEnum.esEU:
      return 'es';
    case LocaleEnum.frEU:
      return 'fr';
    case LocaleEnum.enJP:
    case LocaleEnum.jaJP:
      return 'jp';
    case LocaleEnum.apacUS:
      return 'apac-us';
    case LocaleEnum.enAU:
    case LocaleEnum.enAud:
      return 'au';
    case LocaleEnum.enNZ:
    case LocaleEnum.enNzd:
      return 'nz';

    default:
      return assertNever(portalLocale, 'us');
  }
};

export const useLocaleForAtlas = () => {
  const locale = useLocale();
  return getLocaleForAtlas(locale);
};

export const isOfferInvalidReason404 = (
  reason: OfferInvalidReasonType
): reason is OfferInvalidReason404 => {
  return OFFER_INVALID_REASON_404.includes(reason as OfferInvalidReason404);
};

export const getOfferInvalidReason = (
  offer: PortalOffer,
  locale: LocaleEnum
): OfferInvalidReasonType | null => {
  const restrictions = getOfferAvailabilityRestriction(offer, getLocaleForAtlas(locale));
  if (!restrictions) {
    return OFFER_INVALID_REASON.UnsupportedLocale;
  }

  if (restrictions.is_sold_out) {
    return OFFER_INVALID_REASON.SoldOut;
  }

  const now = new Date().getTime();
  if (restrictions?.date_to) {
    const expiryDate = new Date(restrictions.date_to).getTime();
    if (expiryDate < now) {
      return OFFER_INVALID_REASON.Expired;
    }
  }
  if (restrictions?.date_from) {
    const startDate = new Date(restrictions.date_from).getTime();
    if (now < startDate) {
      return OFFER_INVALID_REASON.TooEarly;
    }
  }
  if (offer.is_paused) {
    return OFFER_INVALID_REASON.Paused;
  }
  return null;
};

// For Portal types (array-based product_data)
export function getOfferSubscriptionProductData<T extends OfferCoreProduct>(
  subOrAddon: Pick<T, 'product_data'>
): T['product_data'] | null;
export function getOfferSubscriptionProductData<T extends OfferAddonProduct>(
  subOrAddon: Pick<T, 'product_data'>
): T['product_data'] | null;

// For Atlas types (object-based product_data)
export function getOfferSubscriptionProductData(
  subOrAddon: Pick<OfferMultipleOptionsCoreProduct, 'product_data'>
): ExtendedProductData;
export function getOfferSubscriptionProductData(
  subOrAddon: Pick<OfferMultipleOptionsAddonProduct, 'product_data'>
): ExtendedProductData;

// Implementation
export function getOfferSubscriptionProductData(subOrAddon: {
  product_data: any;
}): ExtendedProductData | null {
  const { product_data: productData } = subOrAddon;

  if (!productData) return null;

  // Portal format: array
  if (Array.isArray(productData)) {
    return productData.length ? productData[0] : null;
  }

  // Atlas format: single object
  return productData;
}

/**
 * Converts applicable mobile/desktop images from a sanity reference to a image
 * source that can be used directly.
 *
 * Provided {@link media} should be pre-filtered to include only the current
 * locale (see {@link Languages}).
 */
export const getOfferSubscriptionMedia = (
  media:
    | Pick<OfferCoreProductMedia, 'type' | 'sanity_reference_id'>[]
    | Pick<OfferMultipleOptionsCoreProductMedia, 'type' | 'sanity_reference_id'>[],
  parseToImageSource: boolean
) => {
  let desktop: string | null = null;
  let mobile: string | null = null;

  // eslint-disable-next-line no-restricted-syntax
  for (const item of media) {
    if (item.type === OFFER_MEDIA_DEVICE_TYPE.Desktop) {
      desktop ||= item.sanity_reference_id;
    } else if (item.type === OFFER_MEDIA_DEVICE_TYPE.Mobile) {
      mobile ||= item.sanity_reference_id;
    }

    if (desktop && mobile) {
      break;
    }
  }

  if (parseToImageSource) {
    desktop = desktop ? urlFor({ asset: { _ref: desktop, _type: 'reference' } }).url : desktop;
    mobile = mobile ? urlFor({ asset: { _ref: mobile, _type: 'reference' } }).url : mobile;
  }

  return { desktop, mobile };
};

export const getProductType = (quantity: number, isOnetime?: boolean) => {
  if (quantity === 1) return 'single';
  if (quantity === 1 && isOnetime) return 'OTP';
  if (quantity === 2) return 'double';
  if (quantity === 3) return 'family';
  return 'OTP';
};

export const mapFlavorToProductVariantId = (
  store: StoreCode,
  flavor: string,
  productCategory: 'pouch' | 'travel-pack',
  productType: 'single' | 'double' | 'OTP' | 'family',
  coreProduct: OfferCoreProduct
) => {
  const flavorKey =
    flavor.toLowerCase() as keyof (typeof SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53)[StoreCode];

  const variantMap = VARIANT_ID_MAP[productCategory]?.[productType];
  const variantId = variantMap?.[store]?.[flavorKey];

  return {
    quantity: coreProduct.quantity,
    productVariantId: `gid://shopify/ProductVariant/${variantId}`,
    isOnetime: coreProduct.is_onetime_purchase
  };
};

export const mapFlavorImageToMediaOptions = (flavor: string) => {
  const imageId = desktopMediaMap[flavor as keyof typeof desktopMediaMap];
  const mobileImageId = mobileMediaMap[flavor as keyof typeof desktopMediaMap];

  if (!imageId) {
    return {
      preferredMediaOptions: [],
      fallbackMediaOptions: []
    };
  }

  return {
    preferredMediaOptions: [
      { type: OFFER_MEDIA_DEVICE_TYPE.Mobile, sanity_reference_id: mobileImageId },
      { type: OFFER_MEDIA_DEVICE_TYPE.Desktop, sanity_reference_id: imageId }
    ],
    fallbackMediaOptions: [
      { type: OFFER_MEDIA_DEVICE_TYPE.Mobile, sanity_reference_id: mobileImageId },
      { type: OFFER_MEDIA_DEVICE_TYPE.Desktop, sanity_reference_id: imageId }
    ]
  };
};

export const getSellingPlanId = (productData: OfferShopifyProductData, fallback?: string) => {
  const result =
    'sellingPlanId' in productData && typeof productData.sellingPlanId === 'string'
      ? productData.sellingPlanId
      : fallback;

  // Validate that this is a valid selling plan id.
  if (result?.length) {
    const parts = result.split('/');
    const lastPart = parts[parts.length - 1];
    // Invalid selling plan id.
    const numericId = parseInt(lastPart, 10);
    if (Number.isNaN(numericId) || numericId <= 0) {
      return undefined;
    }
    return getFormattedShopifyId('SellingPlan', result);
  }
  return undefined;
};

export const getSellingPlanIdForStoreCode = (storeCode: StoreCode) => {
  switch (storeCode) {
    case 'us':
    case 'au':
    case 'nz':
    case 'jp':
    case 'qa':
    case 'dev':
    case 'aud':
    case 'nzd':
      return 'gid://shopify/SellingPlan/2565374050';

    case 'uk':
    case 'ukd':
      return 'gid://shopify/SellingPlan/4239229005';

    case 'eu':
    case 'eud':
      return 'gid://shopify/SellingPlan/689745461588';

    default:
      assertNever(storeCode);
      return '';
  }
};
